import {
  Icons,
  Pie,
  PieChartContainer,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@cast/design-system';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { CardCurrentDate } from '../_components/CardCurrentDate';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { dashesOrNum, formatPercentage } from '@cast/utils';

import { useAutoscalerImpactData } from './_hooks/useAutoscalerImpactData';

const CARD_TITLE = 'Workload Autoscaler impact';

const COST_TOOLTIP =
  'Workloads optimized for cost through vertical autoscaling (rightsizing / VPA).';
const PERFORMANCE_TOOLTIP =
  'Workloads optimized for performance through horizontal autoscaling (HPA).';

const MetricBox = ({
  label,
  value,
  unit,
  tooltip,
}: {
  label: string;
  value: number | undefined;
  unit: string;
  tooltip: string;
}) => (
  <Stack
    gap={2}
    pl={16}
    sx={{ borderLeft: '1px solid', borderColor: 'grey.200' }}
  >
    <Tooltip title={tooltip} placement="top">
      <Typography
        variant="L10M"
        color="textSecondary"
        sx={{
          textDecoration: 'underline dotted',
          textUnderlineOffset: 2,
          width: 'fit-content',
        }}
      >
        {label}
      </Typography>
    </Tooltip>
    <Stack direction="row" gap={4} alignItems="baseline">
      <Typography variant="h5">{value !== undefined ? value : '—'}</Typography>
      <Typography variant="P12R" color="textSecondary">
        {unit}
      </Typography>
    </Stack>
  </Stack>
);

const AutoscalerImpactCardSkeleton = () => (
  <Stack gap={40}>
    <Stack direction="row" gap={12} alignItems="center">
      <Skeleton variant="circular" width={80} height={80} />
      <Stack gap={8}>
        <Skeleton variant="text" width={80} />
        <Skeleton variant="text" width={160} />
      </Stack>
    </Stack>

    <Stack direction="row" gap={24}>
      <Stack
        gap={2}
        pl={16}
        sx={{ borderLeft: '1px solid', borderColor: 'grey.200' }}
      >
        <Skeleton variant="text" width={60} />
        <Skeleton variant="text" width={100} />
      </Stack>
      <Stack
        gap={2}
        pl={16}
        sx={{ borderLeft: '1px solid', borderColor: 'grey.200' }}
      >
        <Skeleton variant="text" width={80} />
        <Skeleton variant="text" width={100} />
      </Stack>
    </Stack>
  </Stack>
);

export const AutoscalerImpactCard = () => {
  const { data, isLoading, isFetching, error, refetch } =
    useAutoscalerImpactData();
  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  return (
    <WidgetLayout
      title={CARD_TITLE}
      testId="autoscaler-impact-card"
      headerEndContent={<CardCurrentDate />}
    >
      {isCardLoading ? (
        <AutoscalerImpactCardSkeleton />
      ) : hasError ? (
        <FailedToLoad
          title="Failed to load autoscaler impact"
          refresh={refetch}
        />
      ) : (
        <Stack gap={40}>
          {/* Main metric row: pie chart + adoption percentage */}
          <Stack direction="row" gap={12} alignItems="center">
            <PieChartContainer
              testId="autoscaler-impact-pie"
              variant="half-right"
              data={[
                {
                  value: data?.nonOptimizedCount ?? 0,
                  fill: 'green.600',
                },
                {
                  value: data?.costWorkloads ?? 0,
                  fill: 'green.300',
                },
                {
                  value: data?.performanceWorkloads ?? 0,
                  fill: 'green.100',
                  stroke: 'green.200',
                },
              ]}
              radius={40}
            >
              <Pie dataKey="value" />
            </PieChartContainer>

            <Stack gap={8}>
              <Typography variant="h3" data-testid="adoption-percentage">
                {formatPercentage(data?.adoptionPercentage, { roundBy: 1 })}
              </Typography>
              <Stack direction="row" gap={4} alignItems="center">
                <Typography variant="P12M" color="textSecondary">
                  {`${dashesOrNum({
                    value: data?.cpuCoresDifference,
                    roundBy: 2,
                  })} CPU across ${data?.totalCount ?? '—'} workloads`}
                </Typography>
                <Tooltip
                  title="Workloads the Autoscaler upscaled to meet performance needs."
                  placement="top"
                >
                  <Icons.Info size={14} />
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>

          {/* Secondary metric row: cost vs performance workload counts */}
          <Stack direction="row" gap={24}>
            <MetricBox
              label="cost"
              value={data?.costWorkloads}
              unit="workloads"
              tooltip={COST_TOOLTIP}
            />
            <MetricBox
              label="performance"
              value={data?.performanceWorkloads}
              unit="workloads"
              tooltip={PERFORMANCE_TOOLTIP}
            />
          </Stack>
        </Stack>
      )}
    </WidgetLayout>
  );
};
